import { useEffect, useState } from 'react';

import { fetchAdsConfigYaml } from '../api';
import type { AdsConfigYaml } from '../types';

/**
 * Read-only settings page for the ads service. Mirrors the interior
 * pattern: the pipeline config (engine, format presets, mood/style
 * lists, palettes, prompt template) is loaded at server startup from
 * `config_ads.yaml` and surfaced verbatim by `GET /api/ads/api/settings/config-yaml`.
 *
 * Editing happens by changing the YAML file + restarting the service.
 */
export function AdsSettingsPage() {
  const [data, setData] = useState<AdsConfigYaml | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAdsConfigYaml()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((ex) => {
        if (!cancelled) setError(ex instanceof Error ? ex.message : String(ex));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCopy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.yaml);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Ads Settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          Read-only view of the ad-pipeline configuration loaded at startup. To change
          values, edit{' '}
          <code className="font-mono text-xs px-1 py-0.5 rounded bg-slate-100">config_ads.yaml</code>{' '}
          and restart the service.
        </p>
      </div>

      {loading && <div className="text-sm text-slate-400">Loading…</div>}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {data && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-soft overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-slate-50">
            <div className="text-xs font-mono text-slate-500 truncate">{data.path}</div>
            <button
              type="button"
              onClick={handleCopy}
              className="px-2 py-1 rounded text-xs font-medium text-slate-700 hover:bg-white"
            >
              {copied ? 'Copied!' : 'Copy YAML'}
            </button>
          </div>
          <pre className="px-4 py-3 text-xs font-mono text-slate-800 whitespace-pre overflow-x-auto max-h-[70vh] overflow-y-auto leading-relaxed">
            {data.yaml}
          </pre>
        </div>
      )}
    </div>
  );
}
